'use client';

import { useEffect, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';
import Link from 'next/link';
import './talent.css';

/* Saari styling talent.css mein hai.

   IMAGE PATHS (talent.css ke bilkul upar, :root ke andar):
   --talent-hero-bg -> Hero background image
   --talent-cta-bg  -> Final CTA background image

   Images public/assets/images/ mein daalo:
   - talent-hero.jpg -> Hero background
   - talent-cta.jpg  -> Final CTA background

   Links: "People Intelligence in Action" ke 4 cards ka link `metrics` array ke `href` field me badlo. */

type IconName =
  | 'people'
  | 'skills'
  | 'roles'
  | 'projects'
  | 'customers'
  | 'learning'
  | 'finance'
  | 'crm'
  | 'system'
  | 'target'
  | 'work'
  | 'capability'
  | 'bench'
  | 'leadership'
  | 'heart'
  | 'resilient'
  | 'bolt'
  | 'analytics';

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    people: <><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><path d="M16 5a3 3 0 0 1 0 6M18 14c1.8.8 3 2.6 3 5" /></>,
    skills: <><circle cx="12" cy="9" r="5" /><path d="m9 13-2 8 5-3 5 3-2-8" /></>,
    roles: <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 13h18" /></>,
    projects: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M9 4v16M15 4v10" /></>,
    customers: <><path d="M4 21V5l8-2v18M12 21V9l8 2v10" /><path d="M8 9h.01M8 13h.01M8 17h.01M16 14h.01M16 17h.01" /></>,
    learning: <><path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z" /><path d="M4 21V5M9 7h6" /></>,
    finance: <><circle cx="12" cy="12" r="9" /><path d="M12 6v12M15 8.5c-.6-.8-1.5-1.2-2.8-1.2-1.7 0-2.8.8-2.8 2 0 3.1 5.8 1.3 5.8 4.3 0 1.3-1.3 2.1-3 2.1-1.3 0-2.4-.4-3-1.2" /></>,
    crm: <><path d="M4 12a8 8 0 0 1 16 0" /><path d="M4 12v3a2 2 0 0 0 2 2h1v-5H4zM20 12v3a2 2 0 0 1-2 2h-1v-5h3z" /><path d="M18 17c0 2-2 3-5 3" /></>,
    system: <><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M8 15v-3M12 15V8M16 15v-5" /></>,
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>,
    work: <><path d="M3 12h4l3-8 4 16 3-8h4" /></>,
    capability: <><path d="M12 3 3 8l9 5 9-5z" /><path d="m3 12 9 5 9-5" /><path d="m3 16 9 5 9-5" /></>,
    bench: <><path d="M4 10h16v4H4zM6 14v6M18 14v6M4 10V6h16v4" /></>,
    leadership: <><path d="M5 21V4M5 4h11l-2 4 2 4H5" /></>,
    heart: <><path d="M12 20s-8-4.7-8-10.5A4.5 4.5 0 0 1 12 7a4.5 4.5 0 0 1 8 2.5C20 15.3 12 20 12 20z" /></>,
    resilient: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" /></>,
    bolt: <><path d="M13 3 5 14h6l-1 7 8-11h-6z" /></>,
    analytics: <><path d="M4 20V4M4 20h16M8 16v-4M12 16V8M16 16v-6" /></>,
  };

  return (
    <svg
      className="talent-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

function Arrow() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M14 5l7 7m0 0l-7 7m7-7H3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
    </svg>
  );
}

/* ============================== DATA ============================== */

const heroWords = ['PEOPLE', 'SKILLS', 'WORK', 'PERFORMANCE', 'LEARNING', 'GROWTH', 'BUSINESS VALUE'];

const questions = [
  'Why is employee productivity falling?',
  'Who is at risk of leaving?',
  'Where are our critical skill gaps?',
  'Why is hiring taking too long?',
  'Why are new hires slow to become productive?',
  'Where is bench capacity building?',
  'Is workforce cost growing faster than productive capacity?',
  'Is learning actually improving capability?',
  'Do we have the right people for our growth strategy?',
  'How do we build a stronger leadership pipeline?',
];

const dataSources: { icon: IconName; label: string }[] = [
  { icon: 'people', label: 'HR / HXM' },
  { icon: 'projects', label: 'Projects & Timesheets' },
  { icon: 'finance', label: 'Finance & Cost' },
  { icon: 'crm', label: 'CRM & Customers' },
  { icon: 'learning', label: 'Learning (LMS)' },
  { icon: 'system', label: 'Other Systems' },
];

const dataStack = [
  { title: 'Trusted People Data', sub: ['People', 'Organisation', 'Skills', 'Roles', 'Projects', 'Customers', 'Time', 'Performance', 'Learning'] },
  { title: 'Business Context', sub: ['Strategy', 'Workforce Models', 'Skills Taxonomy', 'Industry Benchmarks'] },
];
const dataStackFinal = { title: 'People & Talent Intelligence', sub: ['Analytics', 'AI', 'Insights', 'Scenarios'] };

const decisions: { title: string; text: string; tone: string }[] = [
  { title: 'Better Decisions', text: 'Right People, Right Work, Right Time', tone: 'green' },
  { title: 'Higher Productivity', text: 'More Capability, Lower Attrition', tone: 'blue' },
  { title: 'Stronger Business Value', text: 'People Create Opportunity', tone: 'purple' },
];

const outcomes: { icon: IconName; text: string }[] = [
  { icon: 'analytics', text: 'Higher productivity and utilisation' },
  { icon: 'people', text: 'Lower attrition and higher retention' },
  { icon: 'target', text: 'Right skills at the right time' },
  { icon: 'bolt', text: 'Faster time-to-productivity' },
  { icon: 'work', text: 'Better workforce planning' },
  { icon: 'bench', text: 'Reduced bench cost' },
  { icon: 'leadership', text: 'Stronger leadership pipeline' },
  { icon: 'heart', text: 'Greater employee engagement' },
  { icon: 'resilient', text: 'A future-ready, resilient organisation' },
];

const workedSteps: { icon: IconName; title: string; text: string }[] = [
  { icon: 'target', title: 'Business Strategy', text: 'What do we want to achieve?' },
  { icon: 'work', title: 'Future Work', text: 'What work will we need?' },
  { icon: 'capability', title: 'Capabilities Required', text: 'What capabilities are needed?' },
  { icon: 'roles', title: 'Jobs / Roles', text: 'Which roles deliver this work?' },
  { icon: 'skills', title: 'Skills Required', text: 'What skills are needed?' },
];

/* href = card ke "See / View ..." link ka destination. Abhi chaaron People Intelligence solution page par jaate hain. */
const metrics: { label: string; value: string; desc: string; link: string; tone: string; href: string }[] = [
  { label: 'Attrition Risk', value: '12%', desc: 'employees at risk in next 6 months', link: 'See at-risk talent', tone: 'rose', href: '/solutions/people-intelligence' },
  { label: 'Skill Gaps', value: '32', desc: 'critical skills to build in next 12 months', link: 'View skill gaps', tone: 'purple', href: '/solutions/people-intelligence' },
  { label: 'Time to Hire', value: '28%', desc: 'reduction in time-to-hire', link: 'See hiring insights', tone: 'amber', href: '/solutions/people-intelligence' },
  { label: 'Learning Impact', value: '45%', desc: 'higher completion in priority skills', link: 'View learning impact', tone: 'green', href: '/solutions/people-intelligence' },
];

const problems = [
  'Why is employee productivity falling?',
  'Who is at risk of leaving?',
  'Where are our critical skill gaps?',
  'Why is hiring taking too long?',
  'Why are new hires slow to become productive?',
  'Where is bench capacity building?',
  'Is workforce cost growing faster than productive capacity?',
  'Is learning actually improving capability?',
];
const problemStart = 39;

const systems = [
  { code: 'SAP', label: 'SuccessFactors' },
  { code: 'ERP', label: 'ERP' },
  { code: 'PMO', label: 'Projects & PMO' },
  { code: 'CRM', label: 'CRM' },
  { code: 'LMS', label: 'Learning (LMS)' },
  { code: 'HRIS', label: 'HRIS / Other' },
];

const systemStack = [
  { title: 'Trusted People Data', sub: 'Organisation • Jobs • Skills • Projects • Customers • Market Context' },
  { title: 'Business Context', sub: 'Predictive • Workforce • Scenario Planning' },
  { title: 'AI & Analytics', sub: '' },
];

/* ===================== SCROLL ANIMATION ===================== */
/* Sirf fade-up (neeche se upar) aur zoom. Left/right slide nahi. */
const animGroups: [string, 'fade-up' | 'zoom'][] = [
  ['.sec-head, .opview-head', 'fade-up'],
  ['.q-card', 'fade-up'],
  ['.src-card', 'zoom'],
  ['.stack-item, .stack > .stack-step', 'fade-up'],
  ['.decision-card', 'zoom'],
  ['.eco-card', 'zoom'],
  ['.cockpit', 'fade-up'],
  ['.step-card', 'zoom'],
  ['.metric-card', 'fade-up'],
  ['.ps-card', 'fade-up'],
  ['.problem-row', 'fade-up'],
  ['.sys-card', 'zoom'],
  ['.final-cta-box', 'zoom'],
];

function useScrollReveal(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('rv--in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    animGroups.forEach(([selector, type]) => {
      const perParent = new Map<Element, number>();
      el.querySelectorAll<HTMLElement>(selector).forEach((node) => {
        const parent = node.parentElement as Element;
        const i = perParent.get(parent) ?? 0;
        perParent.set(parent, i + 1);

        node.classList.add('rv', `rv--${type}`);
        node.style.setProperty('--rv-delay', `${(i % 6) * 80}ms`); // stagger
        io.observe(node);
      });
    });

    return () => io.disconnect();
  }, [root]);
}

/* ============================== PAGE ============================== */

export default function Talent() {
  const pageRef = useRef<HTMLDivElement>(null);
  useScrollReveal(pageRef);

  return (
    <div className="talent-page" ref={pageRef}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="talent-hero" data-purpose="hero-section">
          <div className="talent-hero-bg-image"></div>
          <div className="talent-hero-bg-overlay"></div>
          <div className="talent-hero-grid"></div>
          <div className="talent-hero-glow talent-hero-glow--left"></div>
          <div className="talent-hero-glow talent-hero-glow--right"></div>

          <div className="op-container op-container--left talent-hero-inner">
            <div className="talent-hero-content">
              <h1 className="talent-hero-title">
                Know Your People.
                <br />
                Understand Your Capability.
                <br />
                Build What Comes Next.
              </h1>

              <p className="talent-hero-subtitle">Better people. Better performance.</p>

              <p className="talent-hero-text">
                Connect employee, organisation, skills, performance, learning, project and workforce
                data to understand capability, productivity, risk and opportunity — and make better
                people decisions.
              </p>

              <div className="talent-hero-actions">
                <Link className="btn-primary" href="/tellusyourproblem#tell-details">
                  Tell Us Your People &amp; Talent Problem
                  <Arrow />
                </Link>
                <Link className="btn-glass" href="#talent-intelligence">
                  See How It Works
                  <span className="play-icon">▶</span>
                </Link>
              </div>

              <div className="talent-hero-words">
                {heroWords.map((w) => (
                  <span className="talent-hero-word" key={w}>{w}</span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================== THE QUESTIONS LEADERS ASK ================== */}
        <section className="op-section op-section--white" data-purpose="leader-questions" id="talent-intelligence">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">Real Decisions</div>
              <h2 className="sec-title">The Questions Leaders Ask</h2>
              <p className="sec-sub">Real questions. Real decisions. Real impact.</p>
            </div>

            <div className="q-grid">
              {questions.map((q) => (
                <div className="q-card" key={q}>
                  <span className="q-mark">?</span>
                  <p className="q-text">{q}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= FROM DATA TO PEOPLE INTELLIGENCE ================= */}
        <section className="op-section op-section--slate" data-purpose="data-to-intelligence">
          <div className="op-container">
            <div className="sec-head sec-head--center">
              <div className="sec-badge">Connected Data</div>
              <h2 className="sec-title">From Data to People Intelligence</h2>
              <p className="sec-sub sec-sub--base">Connect the dots across your enterprise.</p>
            </div>

            <div className="src-grid">
              {dataSources.map((s) => (
                <div className="src-card" key={s.label}>
                  <div className="src-icon">
                    <Icon name={s.icon} />
                  </div>
                  <span className="src-label">{s.label}</span>
                </div>
              ))}
            </div>

            <div className="stack">
              {dataStack.map((step) => (
                <div className="stack-item" key={step.title}>
                  <div className="stack-step">
                    {step.title}
                    <small>{step.sub.join(' • ')}</small>
                  </div>
                  <span className="stack-arrow">↓</span>
                </div>
              ))}
              <div className="stack-step stack-step--final">
                {dataStackFinal.title}
                <small>{dataStackFinal.sub.join(' • ')}</small>
              </div>
            </div>

            <div className="decision-grid">
              {decisions.map((d) => (
                <div className={`decision-card decision-card--${d.tone}`} key={d.title}>
                  <h3 className="decision-title">{d.title}</h3>
                  <p className="decision-text">{d.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ========================= BUSINESS OUTCOMES ========================= */}
        <section className="op-section op-section--white" data-purpose="business-outcomes">
          <div className="op-container">
            <div className="sec-head sec-head--center">
              <div className="sec-badge">Outcomes</div>
              <h2 className="sec-title">Business Outcomes</h2>
              <p className="sec-sub sec-sub--base">From people data to a stronger enterprise.</p>
            </div>

            <div className="outcome-grid">
              {outcomes.map((o) => (
                <div className="eco-card" key={o.text}>
                  <div className="eco-icon">
                    <Icon name={o.icon} />
                  </div>
                  <h3 className="eco-title">{o.text}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ======================== WORKED QUESTION ======================== */}
        <section className="op-section op-section--slate" data-purpose="worked-question">
          <div className="op-container">
            <div className="opview-head">
              <div>
                <div className="sec-badge">Worked Example</div>
                <h2 className="sec-title">A Worked Question</h2>
                <p className="sec-sub sec-sub--base">
                  From business strategy down to the skills that are missing.
                </p>
              </div>
              <div className="opview-live">
                <span className="opview-live-dot"></span>
                Illustrative Flow
              </div>
            </div>

            <div className="cockpit">
              <div className="cockpit-bar">
                <div className="cockpit-bar-left">
                  <div className="cockpit-light cockpit-light--red"></div>
                  <div className="cockpit-light cockpit-light--amber"></div>
                  <div className="cockpit-light cockpit-light--green"></div>
                  <span className="cockpit-name">ORANE-TALENT-VIEW // SKILL GAP ANALYSIS</span>
                </div>
                <div className="cockpit-bar-right">
                  <span className="cockpit-chip">MODE: ILLUSTRATIVE</span>
                </div>
              </div>

              <h3 className="cockpit-question">“Where are our critical skill gaps?”</h3>

              <div className="step-grid">
                {workedSteps.map((s, i) => (
                  <div className="step-card" key={s.title}>
                    <div className="step-card-head">
                      <span className="step-icon">
                        <Icon name={s.icon} />
                      </span>
                      <span className="step-num">{i + 1}</span>
                    </div>
                    <div className="step-title">{s.title}</div>
                    <div className="step-text">{s.text}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================== PEOPLE INTELLIGENCE IN ACTION ================== */}
        <section className="op-section op-section--white" data-purpose="intelligence-in-action">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">Insight to Impact</div>
              <h2 className="sec-title">People Intelligence in Action</h2>
              <p className="sec-sub">From insight to impact. Examples.</p>
            </div>

            <div className="metric-grid">
              {metrics.map((m) => (
                <div className="metric-card" key={m.label}>
                  <p className="metric-label">{m.label}</p>
                  <p className={`metric-value metric-value--${m.tone}`}>{m.value}</p>
                  <p className="metric-desc">{m.desc}</p>
                  <Link className="metric-link" href={m.href}>
                    {m.link}
                    <Arrow />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ======================== 52 PROBLEMS ======================== */}
        <section className="op-section op-section--slate" data-purpose="business-problems">
          <div className="op-container">
            <div className="ps-card">
              <div className="sec-badge">Relevant 52 Business Problems (39–46)</div>
              <h2 className="sec-title sec-title--sm">Key People &amp; Talent Questions</h2>
              <p className="sec-sub sec-sub--base">Explore the full library.</p>

              <div className="problem-list">
                {problems.map((p, i) => (
                  <div className="problem-row" key={p}>
                    <span className="problem-num">{problemStart + i}</span>
                    <span className="problem-text">{p}</span>
                  </div>
                ))}
              </div>

              <div className="ps-foot">
                <Link className="btn-small" href="/52problem">
                  Explore All 52 Business Problems
                  <Arrow />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ======================= ENTERPRISE SYSTEMS ======================= */}
        <section className="op-section op-section--white" data-purpose="enterprise-systems">
          <div className="op-container">
            <div className="sec-head sec-head--center">
              <div className="sec-badge">Enterprise Systems &amp; Data</div>
              <h2 className="sec-title">Bring Data Together</h2>
              <p className="sec-sub sec-sub--base">
                Bring data together. Add business context. Deliver people intelligence.
              </p>
            </div>

            <div className="sys-grid">
              {systems.map((s) => (
                <div className="sys-card" key={s.label}>
                  <div className="sys-code">{s.code}</div>
                  <span className="sys-label">{s.label}</span>
                </div>
              ))}
            </div>

            <div className="stack">
              {systemStack.map((step) => (
                <div className="stack-item" key={step.title}>
                  <div className="stack-step">
                    {step.title}
                    {step.sub && <small>{step.sub}</small>}
                  </div>
                  <span className="stack-arrow">↓</span>
                </div>
              ))}
              <div className="stack-step stack-step--final">
                People Intelligence
                <small>Decision → Action → Outcome</small>
                <small>From Workforce Data to Enterprise Capability</small>
              </div>
            </div>
          </div>
        </section>

        {/* ========================= FINAL CTA ================================ */}
        <section className="final-cta" data-purpose="final-call-to-action" id="contact">
          <div className="final-cta-bg"></div>
          <div className="final-cta-overlay"></div>
          <div className="op-container final-cta-inner">
            <div className="final-cta-box">
              <div className="final-cta-left">
                <h2 className="final-cta-title">Don’t Just Manage Headcount. Build Enterprise Capability.</h2>
                <p className="final-cta-text">
                  Turn workforce data into decisions on skills, work, performance and value.
                </p>
              </div>
              <div className="final-cta-right">
                <Link className="btn-primary btn-primary--cta" href="/tellusyourproblem#tell-details">
                  Tell Us Your People &amp; Talent Problem
                  <Arrow />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}